import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Eye, EyeOff } from "lucide-react";

import { Button, IconCircle, WaveBackdrop } from "@/components/kit";
import { useApp } from "@/store";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "login" ? "login" : "register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login, register, onboarded, continueAsGuest, completeOnboarding, toast } = useApp();
  const navigate = useNavigate();
  const edit = (setter) => (e) => {
    setter(e.target.value);
    if (error) setError(null);
  };

  const submit = async (e) => {
    e?.preventDefault();
    setError(null);
    if (!email.includes("@")) return setError("Enter a valid email address.");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    setLoading(true);
    try {
      const u = mode === "login" ? await login(email.trim(), password) : await register(name.trim(), email.trim(), password);
      const hasSavedProfile = Object.entries(u.profile ?? {}).some(([k, v]) => k !== "confidences" && (Array.isArray(v) ? v.length > 0 : Boolean(v)));
      if (hasSavedProfile && !onboarded) await completeOnboarding();
      toast(mode === "login" ? "Welcome back." : "Account created.", "success");
      navigate(onboarded || hasSavedProfile ? "/home" : "/questionnaire", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const input = "focus-ring h-[52px] w-full rounded-xl border border-line bg-white px-4 font-body-medium text-[15px] text-ink placeholder:text-mute focus-visible:border-ocean";
  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-off">
      <WaveBackdrop />
      <div className="relative mx-auto max-w-md px-6 pb-10 pt-6">
        <IconCircle testId="auth-back-button" icon={ChevronLeft} label="Back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/", { replace: true }))} />
        <p className="t-label mt-6">BREEZ AI</p>
        <h1 data-testid="auth-title" className="t-h1 mt-1">{mode === "login" ? "WELCOME BACK." : "CREATE YOUR ACCOUNT."}</h1>
        <p className="t-body mt-2">{mode === "login" ? "Sign in to sync your hair profile and saved styles." : "Save your hair profile and styles across devices."}</p>
        <form onSubmit={submit} className="mt-8 flex flex-col gap-4" noValidate>
          {mode === "register" ? (
            <div className="flex flex-col gap-2">
              <label htmlFor="auth-name" className="t-label">Name (optional)</label>
              <input id="auth-name" data-testid="auth-name-input" value={name} onChange={edit(setName)} placeholder="Your first name" autoComplete="given-name" className={input} />
            </div>
          ) : null}
          <div className="flex flex-col gap-2">
            <label htmlFor="auth-email" className="t-label">Email</label>
            <input id="auth-email" data-testid="auth-email-input" type="email" value={email} onChange={edit(setEmail)} placeholder="you@example.com" autoComplete="email" className={input} />
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="auth-password" className="t-label">Password</label>
            <div className="relative">
              <input id="auth-password" data-testid="auth-password-input" type={showPw ? "text" : "password"} value={password} onChange={edit(setPassword)} placeholder="At least 6 characters" autoComplete={mode === "login" ? "current-password" : "new-password"} className={`${input} pr-14`} />
              <button type="button" data-testid="auth-toggle-password" aria-label={showPw ? "Hide password" : "Show password"} onClick={() => setShowPw((s) => !s)} className="focus-ring absolute right-1 top-1 flex h-11 w-11 items-center justify-center rounded-full text-mute">
                {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
          {error ? <p data-testid="auth-error" role="alert" className="t-strong !text-[#EF4444]">{error}</p> : null}
          <Button testId="auth-submit-button" type="submit" title={mode === "login" ? "Sign In" : "Create Account"} loading={loading} />
          <button type="button" data-testid="auth-switch-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(null); }} className="focus-ring rounded-md py-2 t-body text-center">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <span className="t-strong !text-ocean">{mode === "login" ? "Create account" : "Sign in"}</span>
          </button>
          <Button
            testId="auth-guest-button"
            title="Continue as Guest"
            variant="ghost"
            onClick={async () => {
              await continueAsGuest();
              navigate(onboarded ? "/home" : "/questionnaire", { replace: true });
            }}
          />
        </form>
      </div>
    </div>
  );
}
